import { selectAnatomy } from '@chakra-ui/react/anatomy';
import { defineSlotRecipe } from '@chakra-ui/react/styled-system';

import { base, variants } from '../common/ComboboxAndSelect.common';
import { rem } from '../themeUtils';

export const selectSlotRecipe = defineSlotRecipe({
  className: 'select',
  slots: [
    ...selectAnatomy.keys(),
    'searchInputGroup',
    'searchInput',
    'searchClear',
    'actionContainer',
    'action',
    'checkbox',
    'checkmark',
    'itemList',
    'itemContent',
    'itemBadge',
    'itemIcon',
    'valueIcon',
    'avatar',
    'itemLabel',
    'itemHelperText',
    'itemLoading',
    'itemLoadingLabel',
    'emptyState',
    'errorState',
    'errorIcon',
    'errorTextBlock',
    'errorLabel',
  ],
  base: {
    trigger: {
      display: 'flex',
      alignItems: 'center',
      borderWidth: '1',
      borderColor: 'border/strong',
      background: 'background/primary',
      color: 'input/text/inputValue',
      gap: '8',
      justifyContent: 'space-between',
      // 15px + the 1px border = the spec's 16px, measured from the trigger's outer edge.
      // The indicator group below is offset the same way on the end side.
      paddingInlineStart: rem(15),
      paddingInlineEnd: '48',
      paddingBlock: rem(11),
      borderRadius: '4',
      cursor: 'pointer',
      userSelect: 'none',
      textAlign: 'start',
      focusVisibleRing: 'inside',
      width: '100%',
      _open: {
        borderColor: 'border/focus',
      },
      _placeholderShown: {
        color: 'text/secondary',
      },
      _disabled: {
        color: 'text/disabled',
        background: 'background/disabled',
        cursor: 'not-allowed',
      },
      _invalid: {
        borderColor: 'border/error',
      },
      _readOnly: {
        background: 'background/disabled',
        cursor: 'default',
      },
    },
    indicatorGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: '8',
      position: 'absolute',
      insetEnd: 0,
      top: 0,
      bottom: 0,
      paddingInlineStart: '16',
      paddingInlineEnd: rem(15),
      pointerEvents: 'none',
    },
    indicator: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'icon/secondary',
      _disabled: {
        color: 'icon/disabled',
      },
      _readOnly: {
        color: 'icon/on-disabled',
      },
    },
    ...base,
    content: {
      ...base.content,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      // Zag's popper sets `--reference-width` (the trigger width) on the positioner, so the
      // menu is never narrower than the Select input — but can grow wider for long items.
      minWidth: 'var(--reference-width)',
      maxWidth: rem(800),
      focusVisibleRing: 'none',
    },
    item: {
      ...base.item,
      _active: {
        background: 'background/active',
      },
      _selected: {
        background: 'background/selected',
        _highlighted: {
          background: 'background/selected-hover',
        },
      },
      _disabled: {
        cursor: 'not-allowed',
        color: 'text/disabled',
        _highlighted: {
          background: 'transparent',
        },
      },
    },
    control: {
      position: 'relative',
      width: '100%',
    },
    valueText: {
      display: 'flex',
      alignItems: 'center',
      gap: '8',
      flex: '1 1 0',
      minWidth: 0,
      overflow: 'hidden',
    },
    itemList: {
      flex: '1 1 auto',
      maxHeight: 'var(--select-menu-list-max-height)',
      minHeight: 0,
      overflowY: 'auto',
      paddingBlock: '8',
    },
    itemContent: {
      display: 'flex',
      flexDirection: 'column',
      flex: '1 0 0',
      minWidth: 0,
    },
    itemBadge: {
      display: 'inline-flex',
      flexShrink: 0,
    },
    itemLoading: {
      flexShrink: 0,
    },
    itemLoadingLabel: {
      color: 'text/secondary',
      flex: '1 0 0',
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    emptyState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      paddingInline: '16',
    },
    errorState: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12',
      paddingInlineStart: '16',
      paddingInlineEnd: '24',
    },
    errorIcon: {
      color: 'icon/negative',
      flexShrink: 0,
    },
    errorTextBlock: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minWidth: 0,
    },
    errorLabel: {
      color: 'text/body',
      maxWidth: '100%',
      minWidth: 0,
      truncate: true,
    },
    itemIcon: {
      color: 'icon/secondary',
      '[data-state="checked"] &': {
        color: 'icon/interactive',
      },
    },
    // The selected value's icon in the trigger. Mirrors the `indicator` (chevron) state colors —
    // the trigger exposes `data-disabled` / `data-readonly`, so the icon reacts via ancestors.
    valueIcon: {
      color: 'icon/secondary',
      '[data-disabled] &': { color: 'icon/disabled' },
      '[data-readonly] &': { color: 'icon/on-disabled' },
    },
    avatar: {
      flexShrink: 0,
      '[data-disabled] &': { opacity: 0.5 },
    },
    itemLabel: {
      color: 'text/body',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: '100%',
      minWidth: 0,
      '[data-disabled] &': {
        color: 'text/disabled',
      },
    },
    itemHelperText: {
      color: 'text/helper',
      textStyle: 'body/sm/regular',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: '100%',
      minWidth: 0,
      '[data-disabled] &': {
        color: 'text/disabled',
      },
    },
    checkbox: {
      position: 'relative',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      borderWidth: '1',
      borderColor: 'border/regular',
      borderRadius: '4',
      background: 'background/primary',
      boxShadow: 'inset/field',
      color: 'icon/on-color',
      '[data-state=checked] &': {
        background: 'input/checkbox/selection',
        borderColor: 'input/checkbox/selection',
        boxShadow: 'none',
      },
      '[data-disabled] &': {
        background: 'background/disabled',
        borderColor: 'border/disabled',
      },
    },
    checkmark: {
      opacity: 0,
      color: 'icon/on-color',
      '[data-state=checked] &': {
        opacity: 1,
      },
    },
    searchInputGroup: {
      borderBottom: '1px solid',
      borderColor: 'border/minimal',
      background: 'background/primary',
      display: 'flex',
      alignItems: 'center',
      gap: '12',
      paddingInlineStart: '16',
      paddingInlineEnd: '12',
      overflow: 'hidden',
      flexShrink: 0,
      _hover: {
        background: 'background/secondary',
      },
      _focusWithin: {
        background: 'background/primary',
      },
    },
    searchInput: {
      flex: '1 0 0',
      minWidth: 0,
      border: 'none',
      outline: 'none',
      background: 'transparent',
      color: 'input/text/inputValue',
      paddingInline: 0,
      paddingBlock: 0,
      _placeholder: {
        color: 'input/text/placeholder',
      },
    },
    searchClear: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '4',
      color: 'icon/primary',
      cursor: 'pointer',
      flexShrink: 0,
      _hover: {
        background: 'background/hover',
      },
    },
    actionContainer: {
      borderTop: '1px solid',
      borderColor: 'border/minimal',
      paddingBlock: '8',
      flexShrink: 0,
    },
    action: {
      display: 'flex',
      alignItems: 'center',
      gap: '12',
      paddingInlineEnd: '24',
      paddingInlineStart: '16',
      color: 'text/primary',
      width: '100%',
      cursor: 'pointer',
      textAlign: 'start',
      _icon: {
        color: 'icon/secondary',
      },
      _hover: {
        background: 'button/secondary/bg-hover',
        _active: {
          background: 'button/secondary/bg-active',
        },
      },
      _active: {
        background: 'button/secondary/bg-active',
      },
      _disabled: {
        color: 'text/disabled',
        cursor: 'not-allowed',
        _icon: {
          color: 'icon/disabled',
        },
      },
      _focusVisible: {
        outlineOffset: '-3px',
      },
    },
  },

  variants: {
    hasStatusIcon: {
      true: {
        trigger: {
          paddingInlineEnd: '96',
        },
      },
      false: {},
    },
    size: {
      lg: {
        ...variants.size.lg,
        trigger: {
          '&:has([data-slot="avatar"])': { paddingBlock: rem(7), paddingInlineStart: rem(11) },
        },
        checkbox: {
          width: '24',
          height: '24',
        },
        item: {
          ...variants.size.lg.item,
          '&:has([data-slot="avatar"])': {
            paddingInlineStart: '12',
            paddingInlineEnd: '24',
            paddingBlock: '8',
            gap: '8',
          },
          '&:has([data-slot="checkbox"])': {
            minHeight: '48',
            paddingInlineEnd: '24',
            paddingBlock: '12',
            gap: '12',
          },
        },
        content: {
          // 5.5 × 48px item + 16px paddingBlock
          '--select-menu-list-max-height': rem(280),
        },
        itemLabel: {
          textStyle: 'body/lg/regular',
          lineHeight: 'normal',
        },
        itemLoadingLabel: {
          textStyle: 'body/lg/regular',
        },
        emptyState: {
          paddingBlock: '12',
        },
        errorState: {
          paddingBlock: '8',
        },
        errorLabel: {
          textStyle: 'body/lg/regular',
        },
        action: {
          minHeight: '48',
          paddingBlock: '12',
          textStyle: 'body/lg/regular',
          lineHeight: 'normal',
        },
        searchInputGroup: {
          height: rem(56),
          paddingBlock: '16',
        },
        searchInput: {
          textStyle: 'body/lg/regular',
        },
        searchClear: {
          padding: '8',
        },
      },
      md: {
        ...variants.size.md,
        checkbox: {
          width: '20',
          height: '20',
        },
        item: {
          ...variants.size.md.item,
          '&:has([data-slot="avatar"])': {
            paddingInlineStart: '12',
            paddingInlineEnd: '24',
            paddingBlock: '4',
            gap: '8',
          },
          '&:has([data-slot="checkbox"])': {
            minHeight: '40',
            paddingInlineEnd: '24',
            paddingBlock: '8',
            gap: '12',
          },
        },
        content: {
          // 5.5 × 40px item (checkbox/avatar row) + 16px paddingBlock
          '--select-menu-list-max-height': rem(236),
        },
        itemLabel: {
          textStyle: 'body/md/regular',
          lineHeight: 'normal',
        },
        itemLoadingLabel: {
          textStyle: 'body/md/regular',
        },
        emptyState: {
          paddingBlock: '8',
        },
        errorState: {
          paddingBlock: '4',
        },
        errorLabel: {
          textStyle: 'body/md/regular',
        },
        errorIcon: {
          marginBlockStart: '4',
        },
        errorTextBlock: {
          paddingBlock: '2',
        },
        action: {
          minHeight: '40',
          paddingBlock: '8',
          textStyle: 'body/md/regular',
          lineHeight: 'normal',
        },
        trigger: {
          textStyle: 'body/md/regular',
          paddingBlock: rem(9),
          '&:has([data-slot="avatar"])': { paddingBlock: rem(7), paddingInlineStart: rem(11) },
        },
        searchInputGroup: {
          height: '48',
          paddingBlock: '12',
        },
        searchInput: {
          textStyle: 'body/md/regular',
        },
        searchClear: {
          padding: '4',
        },
      },
    },
  },
  defaultVariants: {
    size: 'lg',
  },
});
